import {
  AcceptRaceViewModel,
  EnterInLineViewModel,
  ResponseAPI,
  User,
} from "src/models/user.model";
import {
  AlertController,
  LoadingController,
  LoadingOptions,
  NavController,
  Platform,
} from "@ionic/angular";
import { ApplicationRef, Component, NgZone, OnDestroy } from "@angular/core";
import {
  loadingProps,
  tripRequestLoadingProps,
} from "src/utils/util.functions";
import { ActivatedRoute, Router } from "@angular/router";
import { UtilService } from "src/app/services/util.service";
import { AuthService } from "src/app/services/auth.service";
import { Geolocation } from "@capacitor/geolocation";
import { Languages } from "src/models/language.model";
import { Events } from "src/app/services/events-service.service";
import { OneSignalService } from "src/app/services/oneSignal.service";
import moment from "moment-timezone";
import { StatusBar } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Subscription } from "rxjs";
import { DriverQueueFacadeService } from "src/app/services/driver-queue-facade.service";
import { RaceRequestEvent } from "src/models/signalr-events.model";

@Component({
    selector: "app-info-queue",
    templateUrl: "./info-queue.page.html",
    styleUrls: ["./info-queue.page.scss"],
    providers: [],
    standalone: false
})
export class InfoQueuePage implements OnDestroy {
  // ── Template-bound state ──────────────────────────────────────────────────
  user = {} as User;
  raceInfo = {
    numeroCarrosFila: 0,
    posicaoMotoristaFila: 0,
    numerosCorridaDia: 0,
  };
  isCaller = false;
  refused = false;
  secondsToAccept = 120;
  disputedTripsCount = 0;
  raceRequest: RaceRequestEvent | null = null;
  Languages = Languages;

  // ── Viewmodels (page-owned) ───────────────────────────────────────────────
  enterInLineViewModel = {} as EnterInLineViewModel;
  acceptRaceViewModel = {} as AcceptRaceViewModel;

  // ── Native feature handles ────────────────────────────────────────────────
  private wakeLock: any = null;
  private hasWakelock = false;
  private alarmAudio: HTMLAudioElement;
  private geoWatchId: string = null;
  private readonly QUEUE_NOTIF_ID = 10001;
  private queueNotifActive = false;

  // ── Cleanup ───────────────────────────────────────────────────────────────
  private _subs = new Subscription();
  private appStateListener: any = null;
  private interval: any;
  private disputeInterval: any;
  private dateMoment: moment.Moment;
  private timeStamp: number;
  private deltaDelay: number;
  private dateOpenedPage: Date;

  readonly pathFolderPlayer2 = "assets/sounds/alarm.mp3";

  constructor(
    private _facade: DriverQueueFacadeService,
    public router: Router,
    private route: ActivatedRoute,
    private navCtrl: NavController,
    public util: UtilService,
    public alertController: AlertController,
    private loadingCtrl: LoadingController,
    private _ngZone: NgZone,
    private _applicationRef: ApplicationRef,
    private _event: Events,
    private auth: AuthService,
    private platform: Platform,
    private oneSignalService: OneSignalService,
  ) {}

  ngOnDestroy(): void {
    this._cleanup();
  }

  ionViewWillLeave() {
    clearInterval(this.interval);
    clearInterval(this.disputeInterval);
    this._cleanup();
  }

  async ionViewWillEnter() {
    if (Capacitor.isNativePlatform()) {
      StatusBar.hide().catch(() => {});
      SplashScreen.hide().catch(() => {});
    }

    this.user = this.auth.user;
    this.enterInLineViewModel.driverId = this.user.id;

    await this._enableNativeFeatures();
    await this._facade.setupConnection(this.enterInLineViewModel);

    const ruleResult = await this._facade.validateQueueEntry(this.user.id);
    if (!ruleResult.canEnterQueue) {
      await this.router.navigate(["driver", "queue"]);
      return;
    }

    await this._facade.enterQueue(this.enterInLineViewModel);
    this.enterInLineViewModel.acceptedRace = false;

    this._wireSubscriptions();

    await this._disputedTrips();
    await this._dismissLoad();

    this._facade.coldStart().subscribe();
    await this._uncalledTripTriggerCall();

    this._event.subscribe("triggerManualCaller", () =>
      this.triggerManualCaller(),
    );

    this._subs.add(
      this.platform.resume.subscribe(async () => {
        await this.showLoading(tripRequestLoadingProps);
        await this._facade.reconnect();
        this.dateOpenedPage = new Date();
        await this._uncalledTripTriggerCall();
        await this._dismissLoad();
      }),
    );

    this.appStateListener = await App.addListener(
      "appStateChange",
      async ({ isActive }) => {
        if (this.platform.is("ios")) {
          if (!isActive) {
            await this._showOrUpdateQueueNotification(
              this.raceInfo.posicaoMotoristaFila,
            );
          } else {
            await this._cancelQueueNotification();
          }
        }
      },
    );

    this.disputeInterval = setInterval(() => this._disputedTrips(), 6000);
  }

  // ── Template-bound actions ────────────────────────────────────────────────

  triggerManualCaller() {
    document.getElementById("hiddenDiv")?.click();
  }

  manualCaller() {
    this._dismissLoad2();
    this.dateMoment = moment();
    this.isCaller = false;
    this.refused = false;
    this.raceRequest = JSON.parse(localStorage.getItem("raceRequest"));
    this.enterInLineViewModel.sellerId = this.raceRequest.vendedor.id;
    this.acceptRaceViewModel.raceId = this.raceRequest.id;
    this.acceptRaceViewModel.driverId = this.enterInLineViewModel.driverId;
    this._currentCall(true);
  }

  async acceptRace() {
    this.oneSignalService.clearAll();
    clearInterval(this.interval);
    localStorage.removeItem("raceRequest");
    await this._closeModal();
    await this.showLoading();
    this.secondsToAccept = -1;
    this.enterInLineViewModel.acceptedRace = true;

    const ok = await this._facade.acceptRace(this.acceptRaceViewModel);
    if (!ok) {
      // The accept failed — the offer expired (>120s) or the trip is gone.
      // Dismiss the spinner and re-arm the driver for new offers; otherwise
      // they are stuck on an infinite "please wait" and acceptedRace=true
      // silently drops every future race request.
      await this._dismissLoad();
      this.enterInLineViewModel.acceptedRace = false;
      await this.util.showLongToast("The trip is no longer available.");
    }
    this.acceptRaceViewModel = {} as AcceptRaceViewModel;
    localStorage.removeItem("acceptRace");
  }

  async declineRace() {
    if (this.refused) return;
    this.refused = true;
    this.secondsToAccept = -1;
    this.oneSignalService.clearAll();
    localStorage.removeItem("raceRequest");
    await this._closeModal();

    // The backend RecusarCorrida needs driverId AND sellerId — without them the
    // refusal is silently a no-op and the Decline button "does nothing"
    // (22/06/2026 report 2.1). Populate them defensively with fallbacks.
    this.enterInLineViewModel.driverId ??= this.user?.id;
    this.enterInLineViewModel.sellerId ??= this.raceRequest?.vendedor?.id;
    this.enterInLineViewModel.telefone = this.raceRequest?.celular;
    this.enterInLineViewModel.nomePassageiro = this.raceRequest?.nomePassageiro;
    this.enterInLineViewModel.raceId = this.raceRequest?.id;
    await this._facade.declineRace(this.enterInLineViewModel);
  }

  async leaveLine() {
    await this.showLoading();
    this._stopBackgroundGeolocation();
    await this._cancelQueueNotification();
    await this._facade.leaveQueue(this.enterInLineViewModel);
    const currentPage = this.router.url;
    if (currentPage !== "/driver/queue") {
      this._releaseWakeLock();
      this.enterInLineViewModel.acceptedRace = false;
      await this.router.navigate(["driver", "queue"]);
    }
    await this._dismissLoad();
  }

  async goToScheduledTrips() {
    await this.router.navigate(["call", "scheduled-trips"]);
  }

  async showLoading(opts: LoadingOptions = loadingProps) {
    const loading = await this.loadingCtrl.create(opts);
    return await loading.present();
  }

  // ── Private: event wiring ─────────────────────────────────────────────────

  private _wireSubscriptions(): void {
    // Queue position updates → raceInfo + notification
    this._subs.add(
      this._facade.queuePosition$.subscribe((pos) => {
        this.raceInfo.posicaoMotoristaFila = pos;
        this._showOrUpdateQueueNotification(pos);
      }),
    );

    this._subs.add(
      this._facade.queueSize$.subscribe((size) => {
        this.raceInfo.numeroCarrosFila = size;
      }),
    );

    // Incoming race request → show caller modal
    this._subs.add(
      this._facade.activeRequest$.subscribe(async (request) => {
        await this._receivedRaceRequest(request);
      }),
    );

    // Trip accepted → navigate
    this._subs.add(
      this._facade.tripAccepted$.subscribe(async (onAccept) => {
        await this._driverAcceptedRace(onAccept);
      }),
    );

    // Caller cancelled trip → toast + reenter
    this._subs.add(
      this._facade.callerCancelledRace$.subscribe(async (cancelledRace) => {
        await this._callerCancelledRace(cancelledRace);
      }),
    );

    // Driver kicked → navigate to queue
    this._subs.add(
      this._facade.driverKicked$.subscribe(async (kicked) => {
        if (kicked) {
          const url = this.router.url;
          await this._facade.leaveQueue(this.enterInLineViewModel);
          if (
            url === "/driver/information-queue" ||
            url === "/call/call-driver"
          ) {
            await this.router.navigate(["driver", "queue"]);
          }
        }
      }),
    );

    // Connection verified ping → refresh notification
    this._subs.add(
      this._facade.connectionVerified$.subscribe(async () => {
        await this._showOrUpdateQueueNotification(
          this.raceInfo.posicaoMotoristaFila,
        );
      }),
    );

    // Disputed trips from hub
    this._subs.add(
      this._facade.disputedTripsFromHub$.subscribe((trips) => {
        this.disputedTripsCount = trips?.length ?? 0;
      }),
    );
  }

  // ── Private: hub event handlers ──────────────────────────────────────────

  private async _receivedRaceRequest(
    raceRequest: RaceRequestEvent,
  ): Promise<void> {
    if (this.enterInLineViewModel.acceptedRace) return;
    // Ignore a stale request whose acceptance window already elapsed. A live
    // dispatch always arrives fresh; an old dataRequisicao means this is a
    // replay (e.g. from the BehaviorSubject behind activeRequest$ on re-entry)
    // of an already-finished trip — accepting it just fails with "no longer
    // available". Mirrors the _uncalledTripTriggerCall freshness guard.
    const raceDateMoment = moment(raceRequest?.dataRequisicao ?? new Date());
    if (moment().diff(raceDateMoment, "seconds") > this.secondsAcceptance) {
      return;
    }
    if (!this.hasWakelock) await this._acquireWakeLock();
    this.refused = false;
    this.dateMoment = moment();
    localStorage.setItem("raceRequest", JSON.stringify(raceRequest));

    await this._ngZone.run(async () => {
      this.raceRequest = raceRequest;
      this._applicationRef.tick();
      this.enterInLineViewModel.sellerId = raceRequest.vendedor?.id;
      this.acceptRaceViewModel.raceId = raceRequest.id;
      this.acceptRaceViewModel.driverId = this.enterInLineViewModel.driverId;
      localStorage.setItem(
        "enterInLine",
        JSON.stringify(this.enterInLineViewModel),
      );
      localStorage.setItem(
        "acceptRace",
        JSON.stringify(this.acceptRaceViewModel),
      );

      if (this.raceRequest) {
        await this._caller();
      }
    });
  }

  private async _driverAcceptedRace(onAccept: RaceRequestEvent): Promise<void> {
    await this._dismissLoad();

    if (!onAccept.raceType?.includes("Scheduled")) {
      this._facade.coldStart().subscribe();
      localStorage.setItem("currentRace", JSON.stringify(onAccept));
      await this.router.navigate(["/driver", "start"], {
        relativeTo: this.route,
        skipLocationChange: true,
      });
      await this._dismissLoad();
    } else {
      await this.util.showLongToast(
        `The trip has been scheduled for: ${new Date(
          onAccept.dataAgendamentoCorrida,
        ).toLocaleString("en-US", { hour12: true })}`,
      );
      localStorage.setItem(
        new Date(onAccept.dataAgendamentoCorrida).getTime().toString(),
        JSON.stringify(onAccept),
      );
      await this._dismissLoad();
      await this.leaveLine();
    }
  }

  private async _callerCancelledRace(cancelledRace: any): Promise<void> {
    await this.util.showLongToast(
      `Seller: ${cancelledRace.vendedor?.nome} \n` +
        `Cancelled the trip to ${cancelledRace.enderecoDestino} \n` +
        `Reason: ${cancelledRace.motivoCancelamento}`,
    );
    await this._facade.reenterQueue(this.enterInLineViewModel);
    await this._dismissLoad();
    await this.navCtrl.navigateBack("driver/information-queue");
  }

  // ── Private: countdown ───────────────────────────────────────────────────

  private async _caller(): Promise<void> {
    this.raceRequest = JSON.parse(localStorage.getItem("raceRequest"));
    const currentPage = this.router.url;
    if (currentPage !== "/driver/information-queue") {
      await this.router.navigate(["driver", "information-queue"]);
      await this._ngZone.run(() => this.triggerManualCaller());
    } else {
      await this._currentCall(true);
    }
  }

  private async _currentCall(invokeNgZone: boolean): Promise<void> {
    clearInterval(this.interval);
    const raceDateMoment = moment(
      this.raceRequest?.dataRequisicao ?? new Date(),
    );
    const diffInSecs = this.dateMoment.diff(raceDateMoment, "seconds");
    this.secondsToAccept = this.raceRequest?.dataRequisicao
      ? this.secondsAcceptance - diffInSecs
      : this.secondsAcceptance;
    this.secondsToAccept = Math.min(
      this.secondsToAccept,
      this.secondsAcceptance,
    );

    const show = () => {
      this.isCaller = true;
    };
    if (this.platform.is("ios")) {
      show();
    } else if (invokeNgZone) {
      await this._ngZone.run(async () => {
        show();
        this._applicationRef.tick();
      });
    } else {
      show();
    }

    if (this.alarmAudio) {
      this.alarmAudio.currentTime = 0;
      this.alarmAudio.loop = true;
      this.alarmAudio.play().catch((e) => console.warn("Audio play failed", e));
    }
    this._countdownPwa();
  }

  private _countdownPwa(): void {
    const raceDateMoment = moment(
      this.raceRequest?.dataRequisicao ?? new Date(),
    );
    const diffInSecs = this.dateMoment.diff(raceDateMoment, "seconds");
    let secs = this.raceRequest?.dataRequisicao
      ? this.secondsAcceptance - diffInSecs
      : this.secondsAcceptance;
    secs = Math.min(secs, this.secondsAcceptance);

    clearInterval(this.interval);
    this.timeStamp = Math.floor(Date.now() / 1000);
    this.deltaDelay = 1;
    this.interval = setTimeout(() => this._startCountdown(secs), 1000);
  }

  private async _startCountdown(p0: number): Promise<void> {
    if (
      this.secondsToAccept !== p0 &&
      Math.floor(Date.now() / 1000) - this.timeStamp > this.deltaDelay
    ) {
      this.secondsToAccept -= Math.floor(Date.now() / 1000) - this.timeStamp;
    } else {
      this.secondsToAccept -= 1;
    }
    this.timeStamp = Math.floor(Date.now() / 1000);

    if (this.secondsToAccept <= 0 && !this.enterInLineViewModel.acceptedRace) {
      this.secondsToAccept = -1;
      clearInterval(this.interval);
      await this.declineRace();
    } else if (this.secondsToAccept > 0) {
      this.interval = setTimeout(() => this._startCountdown(p0), 1000);
    }
  }

  private async _closeModal(): Promise<void> {
    this.isCaller = false;
    clearInterval(this.interval);
    if (this.alarmAudio) {
      this.alarmAudio.pause();
      this.alarmAudio.currentTime = 0;
    }
    localStorage.removeItem("raceRequest");
  }

  // ── Private: HTTP helpers ─────────────────────────────────────────────────

  private async _disputedTrips(): Promise<void> {
    this._facade.getDisputedTrips().subscribe((trips: any[]) => {
      this.disputedTripsCount = trips?.length ?? 0;
    });
  }

  private async _uncalledTripTriggerCall(): Promise<void> {
    this._facade.getUncalledTrip().subscribe(async (response: ResponseAPI) => {
      if (!response?.success) return;
      const trip = response.data;
      if (!trip || this.isCaller) return;

      const raceDateMoment = moment(trip?.dataRequisicao ?? new Date());
      const diffInSecs = moment().diff(raceDateMoment, "seconds");
      if (diffInSecs > this.secondsAcceptance) return;

      localStorage.setItem("raceRequest", JSON.stringify(trip));
      this.dateMoment = moment();
      if (this.router.url !== "/driver/information-queue") {
        await this.router.navigate(["driver", "information-queue"]);
      }
      this.triggerManualCaller();
    });
  }

  // ── Private: native features ─────────────────────────────────────────────

  private async _enableNativeFeatures(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await this._createQueueNotifChannel();
      await LocalNotifications.requestPermissions();
      await this._acquireWakeLock();
      await this._enableBackgroundGeolocation();
    } else {
      try {
        const pos = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
        });
        this.enterInLineViewModel.latitude = pos.coords.latitude.toString();
        this.enterInLineViewModel.longitude = pos.coords.longitude.toString();
      } catch (e) {
        console.error("Geolocation error", e);
      }
    }
    this.alarmAudio = new Audio(this.pathFolderPlayer2);
    this.alarmAudio.load();
  }

  private async _acquireWakeLock(): Promise<void> {
    try {
      if ("wakeLock" in navigator) {
        this.wakeLock = await (navigator as any).wakeLock.request("screen");
        this.hasWakelock = true;
        this.wakeLock.addEventListener("release", () => {
          this.hasWakelock = false;
        });
      }
    } catch (e) {
      console.warn("Wake lock not available", e);
    }
  }

  private _releaseWakeLock(): void {
    if (this.wakeLock) {
      this.wakeLock.release().catch(() => {});
      this.wakeLock = null;
      this.hasWakelock = false;
    }
  }

  private async _createQueueNotifChannel(): Promise<void> {
    if (!this.platform.is("android")) return;
    await LocalNotifications.createChannel({
      id: "queue_status",
      name: "Queue Status",
      description: "Shows your current position in the driver queue",
      importance: 3,
      visibility: 1,
      sound: undefined,
      vibration: false,
    }).catch(() => {});
  }

  private async _showOrUpdateQueueNotification(
    position: number,
  ): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: this.QUEUE_NOTIF_ID,
            title: "GoSpread - You are in the queue",
            body:
              position > 0
                ? `Current position: ${position}`
                : "Waiting for queue position...",
            ongoing: true,
            autoCancel: false,
            channelId: "queue_status",
            schedule: { at: new Date(Date.now() + 200) },
          },
        ],
      });
      this.queueNotifActive = true;
    } catch (e) {
      console.warn("Queue notification error", e);
    }
  }

  private async _cancelQueueNotification(): Promise<void> {
    if (!this.queueNotifActive) return;
    await LocalNotifications.cancel({
      notifications: [{ id: this.QUEUE_NOTIF_ID }],
    }).catch(() => {});
    this.queueNotifActive = false;
  }

  private async _enableBackgroundGeolocation(): Promise<void> {
    try {
      await Geolocation.requestPermissions();
      this.geoWatchId = await Geolocation.watchPosition(
        { enableHighAccuracy: true, timeout: 10000 },
        async (position, err) => {
          if (err || !position) return;
          this.enterInLineViewModel.latitude =
            position.coords.latitude.toString();
          this.enterInLineViewModel.longitude =
            position.coords.longitude.toString();
          if (!this.queueNotifActive) {
            await this._showOrUpdateQueueNotification(
              this.raceInfo.posicaoMotoristaFila,
            );
          }
          this._facade
            .updateLocation(
              this.auth.user.id,
              position.coords.latitude.toString(),
              position.coords.longitude.toString(),
              position.coords.heading ?? 0,
            )
            .subscribe();
        },
      );
    } catch (e) {
      console.error("Background geolocation error", e);
    }
  }

  private _stopBackgroundGeolocation(): void {
    if (this.geoWatchId) {
      Geolocation.clearWatch({ id: this.geoWatchId }).catch(() => {});
      this.geoWatchId = null;
    }
  }

  private async _dismissLoad(): Promise<void> {
    if (await this.loadingCtrl.getTop()) {
      await this.loadingCtrl.dismiss();
    }
  }

  private async _dismissLoad2(): Promise<void> {
    while (await this.loadingCtrl.getTop()) {
      await this.loadingCtrl.dismiss();
    }
  }

  private readonly secondsAcceptance = 120;

  private _cleanup(): void {
    this._subs.unsubscribe();
    this._subs = new Subscription();
    this._releaseWakeLock();
    this._stopBackgroundGeolocation();
    this._cancelQueueNotification();
    this.appStateListener?.remove();
    this.appStateListener = null;
    if (this.alarmAudio) {
      this.alarmAudio.pause();
      this.alarmAudio.currentTime = 0;
    }
  }
}
